import { App } from "vue";

/**
 * Microsoft Clarity analytics integration
 * Provides user session recording and analytics
 */
export function createClarityClient(_app: App<Element>): {
    initialize: () => void;
    setUserId: (userId: string) => void;
    setCustomTag: (key: string, value: string) => void;
} {
    const projectId = import.meta.env.VITE_CLARITY_PROJECT_ID;

    if (!projectId) {
        console.warn("Clarity: Project ID not found in environment variables");
        return {
            initialize: () => {},
            setUserId: () => {},
            setCustomTag: () => {},
        };
    }

    let clarityLoaded = false;

    /**
     * Initialize Clarity analytics
     * Loads the Clarity script asynchronously and non-blocking
     */
    function initialize(): void {
        if (clarityLoaded) {
            return;
        }

        try {
            // Bootstrap shim: queue calls before the script loads
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const w = window as any;
            if (typeof w.clarity !== "function") {
                const q: unknown[] = [];
                const shim = function (...args: unknown[]) {
                    q.push(args);
                } as unknown as (...args: unknown[]) => void;
                (shim as any).q = q;
                w.clarity = shim;
            }

            // Load Clarity script asynchronously
            const script = document.createElement("script");
            script.type = "text/javascript";
            script.async = true;
            script.src = `https://www.clarity.ms/tag/${projectId}`;
            script.onload = () => {
                clarityLoaded = true;
                console.log("Clarity analytics initialized");
            };
            script.onerror = (error) => {
                console.error("Failed to load Clarity script:", error);
            };

            document.head.appendChild(script);
        } catch (error) {
            console.error("Error initializing Clarity:", error);
        }
    }

    /**
     * Set user ID for Clarity analytics
     * @param userId - The user ID to track
     */
    function setUserId(userId: string): void {
        if (typeof window !== "undefined" && (window as any).clarity) {
            try {
                // prefer identify API so sessions are tied to clarit
                (window as any).clarity("identify", userId);
            } catch (error) {
                console.error("Error identifying user in Clarity:", error);
            }
        }
    }

    /**
     * Set custom tag for Clarity analytics
     * @param key - The tag key
     * @param value - The tag value
     */
    function setCustomTag(key: string, value: string): void {
        if (typeof window !== "undefined" && (window as any).clarity) {
            try {
                (window as any).clarity("set", key, value);
            } catch (error) {
                console.error("Error setting Clarity custom tag:", error);
            }
        }
    }

    return {
        initialize,
        setUserId,
        setCustomTag,
    };
}

/**
 * Initialize Clarity with user information
 * @param userId - The user ID to track
 * @param userEmail - The user email (optional)
 * @param organizationId - The organization ID (optional)
 */
export function initializeClarityWithUser(
    userId: string,
    userEmail?: string,
    organizationId?: string,
): void {
    const clarity = createClarityClient({} as App<Element>);

    clarity.initialize();

    // Set user identification
    clarity.setUserId(userId);

    if (userEmail) {
        clarity.setCustomTag("userEmail", userEmail);
    }

    if (organizationId) {
        clarity.setCustomTag("organizationId", organizationId);
    }
}
